import { vorschlagEmpfaenger, type EmpfaengerQuellen, type EmpfaengerZuordnung } from './empfaenger';
import { HINWEIS_ART_LABEL } from './planungLogic';
import type {
    BedenkenanzeigeErgebnis,
    BedenkenStempel,
    VersandAuftrag,
    VersandEmpfaenger,
    VersandErgebnis,
} from './planungVersand';
import type { HinweisArt, NeuerPlanungshinweis, Planungshinweis } from './types';

/**
 * Vorbelegung des Versands einer Bedenkenanzeige (Plan Stufe 4.2) — alles,
 * was `HinweisEditor.tsx` fuer den Versanddialog braucht und ohne DOM
 * entscheidbar ist: Dateiname, Ablagetitel, Fassungskennung, Mailbetreff
 * und -text, der vorbelegte Empfaenger und die Pruefung der Eingaben.
 *
 * Pure Funktionen, weil das Projekt ohne jsdom testet (Hausregel „duenner
 * Container, Logik in puren Funktionen"). Der eigentliche Versand steht in
 * `planungVersand.ts` (Stufe 4.1); hier wird nur vorbereitet.
 */

const ARTEN: ReadonlyArray<HinweisArt> = ['hinweis', 'bedenken', 'gefahr'];

/** Kurzform der Art fuer Dateinamen — ohne Leerzeichen, ohne Schraegstrich. */
const ART_DATEI: Record<HinweisArt, string> = {
    hinweis: 'Hinweis',
    bedenken: 'Bedenkenanzeige',
    gefahr: 'Gefahrenanzeige',
};

const artSicher = (art: HinweisArt | string | null | undefined): HinweisArt =>
    ((ARTEN as readonly string[]).includes(String(art)) ? art : 'hinweis') as HinweisArt;

const sauber = (s: string | null | undefined): string => (s == null ? '' : String(s)).trim();

const nummerVon = (h: { nummer?: number | null }): number | null =>
    typeof h.nummer === 'number' && Number.isFinite(h.nummer) ? h.nummer : null;

/** „Bedenkenanzeige Nr. 3" bzw. ohne Nummer nur die Art. */
export const hinweisBezeichnung = (h: Pick<NeuerPlanungshinweis, 'art'> & { nummer?: number | null }): string => {
    const label = HINWEIS_ART_LABEL[artSicher(h.art)];
    const n = nummerVon(h);
    return n !== null ? `${label} Nr. ${n}` : label;
};

/**
 * Dateiname der PDF.
 *
 * Ohne Sonderzeichen und ohne Leerzeichen wie `klaerungslisteDateiname`: der
 * Name landet als Objektname im Storage-Bucket, und dort ist ein
 * Schraegstrich aus einem Projektnamen ein neuer Ordner. Ohne Nummer
 * (ungespeicherter Entwurf) steht „Entwurf" statt einer Zahl.
 */
export const hinweisDateiname = (
    h: Pick<NeuerPlanungshinweis, 'art'> & { nummer?: number | null },
    projektKennung: string | null | undefined,
    datumIso: string | null | undefined,
): string => {
    const rein = (s: string) => sauber(s)
        .replace(/[^\wÄÖÜäöüß\- ]+/g, '')
        .trim()
        .replace(/\s+/g, '_')
        .slice(0, 60);
    const n = nummerVon(h);
    const teile = [ART_DATEI[artSicher(h.art)], n !== null ? `Nr${n}` : 'Entwurf', rein(projektKennung || '')];
    teile.push(sauber(datumIso).slice(0, 10) || 'ohne-Datum');
    return `${teile.filter(Boolean).join('_')}.pdf`;
};

/** Hoechstlaenge des Ablagetitels — laengere Betreffs werden gekuerzt, nicht abgeschnitten. */
const TITEL_MAX = 120;

/** Titel in der Ablage: „Bedenkenanzeige Nr. 3 – Betreff". */
export const hinweisTitel = (
    h: Pick<NeuerPlanungshinweis, 'art' | 'betreff'> & { nummer?: number | null },
): string => {
    const kopf = hinweisBezeichnung(h);
    const betreff = sauber(h.betreff);
    if (!betreff) return kopf;
    const voll = `${kopf} – ${betreff}`;
    return voll.length <= TITEL_MAX ? voll : `${voll.slice(0, TITEL_MAX - 1).trimEnd()}…`;
};

/**
 * Fassungskennung fuer die Ablage (`fassung_ref`, zusammen mit `erzeugt_aus`
 * je Projekt eindeutig). Ein Hinweis wird genau einmal versendet
 * (`versendet_am` wird nie ueberschrieben) — seine Kennung ist deshalb die
 * Satz-Id, lesbar mit Art und Nummer davor. Ohne Id (ungespeicherter
 * Entwurf) gibt es keine Fassung und damit keine Kennung.
 */
export const hinweisFassungRef = (
    h: Pick<NeuerPlanungshinweis, 'art'> & { id?: string | null; nummer?: number | null },
): string | null => {
    const id = sauber(h.id);
    if (!id) return null;
    const n = nummerVon(h);
    return `${artSicher(h.art)}-${n !== null ? n : 'x'}@${id}`;
};

export interface HinweisProjektBezug {
    projektName?: string | null;
    projektNummer?: string | null;
}

/** Projektangabe fuer Betreff und Text: „Projekt 26071002 Neubau" / nur eines / leer. */
const projektText = (p: HinweisProjektBezug | null | undefined): string => {
    const nummer = sauber(p?.projektNummer);
    const name = sauber(p?.projektName);
    if (nummer && name) return `Projekt ${nummer} ${name}`;
    if (nummer) return `Projekt ${nummer}`;
    if (name) return `Projekt ${name}`;
    return '';
};

/** Vorbelegter Mailbetreff: „Bedenkenanzeige Nr. 3: Betreff – Projekt 26071002 Neubau". */
export const hinweisMailBetreff = (
    h: Pick<NeuerPlanungshinweis, 'art' | 'betreff'> & { nummer?: number | null },
    projekt?: HinweisProjektBezug | null,
): string => {
    const kopf = hinweisBezeichnung(h);
    const betreff = sauber(h.betreff);
    const p = projektText(projekt);
    const mitte = betreff ? `${kopf}: ${betreff}` : kopf;
    return p ? `${mitte} – ${p}` : mitte;
};

/** ISO-Tag → TT.MM.JJJJ durch reines Zerlegen (keine Zeitzonenfalle). */
const tagDeutsch = (iso: string | null | undefined): string => {
    const t = sauber(iso).slice(0, 10);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(t)) return '';
    const [j, m, d] = t.split('-');
    return `${d}.${m}.${j}`;
};

export interface HinweisMailAbsender {
    /** Name des Koordinators (Grusszeile). */
    name?: string | null;
    /** Firmenname (Grusszeile). */
    firma?: string | null;
}

/**
 * Vorbelegter Mailtext. Bewusst knapp: der Inhalt steht im angehaengten
 * Blatt, die Mail nennt nur, was anhaengt, bis wann geantwortet werden soll
 * und wer schreibt. Keine Paragraphen — die stehen nur im PDF und dort nur
 * aus dem Datensatz.
 */
export const hinweisMailText = (
    h: Pick<NeuerPlanungshinweis, 'art' | 'betreff' | 'frist_am'> & { nummer?: number | null },
    projekt?: HinweisProjektBezug | null,
    absender?: HinweisMailAbsender | null,
): string => {
    const art = artSicher(h.art);
    const bezeichnung = hinweisBezeichnung(h);
    const p = projektText(projekt);
    const frist = tagDeutsch(h.frist_am);
    const betreff = sauber(h.betreff);

    const was = art === 'hinweis' ? 'unseren Hinweis' : art === 'gefahr' ? 'unsere Gefahrenanzeige' : 'unsere Bedenkenanzeige';
    const absaetze: string[] = ['Sehr geehrte Damen und Herren,'];
    absaetze.push(
        `anbei erhalten Sie ${was} (${bezeichnung})${betreff ? ` zum Thema „${betreff}"` : ''}`
        + `${p ? ` – ${p}` : ''}.`,
    );
    if (art !== 'hinweis') {
        absaetze.push(frist
            ? `Wir bitten um Ihre schriftliche Stellungnahme und Entscheidung bis zum ${frist}. Was bei Fristablauf geschieht, ist im Anhang genannt.`
            : 'Wir bitten um Ihre schriftliche Stellungnahme und Entscheidung. Die Einzelheiten stehen im Anhang.');
    } else if (frist) {
        absaetze.push(`Über eine Rückmeldung bis zum ${frist} würden wir uns freuen.`);
    }
    const gruss = ['Mit freundlichen Grüßen'];
    const name = sauber(absender?.name);
    const firma = sauber(absender?.firma);
    if (name) gruss.push(name);
    if (firma) gruss.push(firma);
    absaetze.push(gruss.join('\n'));
    return absaetze.join('\n\n');
};

/** Woher der vorbelegte Empfaenger stammt. */
export type EmpfaengerHerkunft = 'hinweis' | 'zuordnung' | 'stammdaten' | null;

export interface EmpfaengerVorbelegung {
    name: string;
    email: string;
    herkunft: EmpfaengerHerkunft;
    /** Kurzer Grund fuer die Anzeige neben dem Feld. */
    grund: string;
}

/**
 * Der vorbelegte Empfaenger einer Bedenkenanzeige — immer der Bauherr.
 *
 * Reihenfolge: was am Hinweis selbst steht (gespeichert oder getippt), sonst
 * die Empfaenger-Zuordnung BH der Planungsphase, sonst der Bauherr aus den
 * Projektstammdaten (`clientName`/`clientEmail` ueber `vorschlagEmpfaenger`).
 * Nichts davon wird automatisch gespeichert — der Mensch uebernimmt per Klick.
 */
export const hinweisEmpfaengerVorbelegung = (
    entwurf: Pick<NeuerPlanungshinweis, 'empfaenger_name' | 'empfaenger_email'> | null | undefined,
    zuordnung?: EmpfaengerZuordnung | null,
    quellen?: EmpfaengerQuellen | null,
): EmpfaengerVorbelegung => {
    const name = sauber(entwurf?.empfaenger_name);
    if (name) {
        return { name, email: sauber(entwurf?.empfaenger_email), herkunft: 'hinweis', grund: 'am Hinweis eingetragen' };
    }
    const bh = zuordnung ? zuordnung.BH : undefined;
    if (bh && sauber(bh.name)) {
        return { name: sauber(bh.name), email: sauber(bh.email), herkunft: 'zuordnung', grund: 'Empfänger-Zuordnung „Bauherr" der Planungsphase' };
    }
    const vorschlag = vorschlagEmpfaenger('BH', quellen)[0];
    if (vorschlag && vorschlag.eintrag.quelle === 'bauherr') {
        return { name: vorschlag.eintrag.name, email: sauber(vorschlag.eintrag.email), herkunft: 'stammdaten', grund: vorschlag.grund };
    }
    return { name: '', email: '', herkunft: null, grund: 'kein Bauherr hinterlegt' };
};

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Was am Hauptempfaenger (dem Bauherrn am Hinweis) fuer einen Versand per
 * Mail nicht stimmt — oder `null`. Anders als bei der Zuordnung
 * (`pruefeEmpfaengerEingabe`) ist die E-Mail hier PFLICHT: ohne sie gibt es
 * keinen Versand und keinen Nachweis. Die ganze Empfaengerliste prueft
 * danach noch `pruefeVersandEmpfaenger` in `planungVersand.ts`.
 */
export const pruefeHauptempfaenger = (name: string, email: string): string | null => {
    if (!sauber(name)) return 'Bitte den Empfänger (Bauherr) eintragen.';
    const e = sauber(email);
    if (!e) return 'Ohne E-Mail-Adresse des Empfängers ist kein Versand möglich.';
    if (!EMAIL.test(e)) return 'Die E-Mail-Adresse des Empfängers ist nicht vollständig.';
    return null;
};

/**
 * Weitere Empfaenger („Kopie an") aus einem Textfeld — je Zeile eine Adresse,
 * wahlweise als `Name <adresse>`. Leere Zeilen und Doppelte entfallen; eine
 * unbrauchbare Zeile wird mit ihrem Wortlaut gemeldet, nicht still verworfen.
 */
export const kopienAus = (text: string | null | undefined): { empfaenger: VersandEmpfaenger[]; fehler: string[] } => {
    const empfaenger: VersandEmpfaenger[] = [];
    const fehler: string[] = [];
    sauber(text).split(/[\n;,]+/).map((z) => z.trim()).filter(Boolean).forEach((zeile) => {
        const m = zeile.match(/^(.*?)<([^<>]+)>$/);
        const email = sauber(m ? m[2] : zeile);
        const name = sauber(m ? m[1] : '') || email;
        if (!EMAIL.test(email)) {
            fehler.push(`„${zeile}" ist keine E-Mail-Adresse.`);
            return;
        }
        if (empfaenger.some((e) => e.email.toLowerCase() === email.toLowerCase())) return;
        empfaenger.push({ name, email });
    });
    return { empfaenger, fehler };
};

/**
 * Was ein Versandlauf fuer DIESEN Hinweis hinterlassen hat — die Grundlage
 * der Sperre gegen einen zweiten Versand an alle (Codex-Review Stufe 4, P2):
 * sobald EIN Empfaenger erreicht ist, darf derselbe Satz nur noch an die
 * nicht erreichten Adressen gehen; der Stempel wird mit dem Zeitpunkt des
 * ERSTEN Erfolgs nachgeholt, nicht mit dem eines Wiederholungslaufs.
 */
export interface VersandStand {
    /** Satz, fuer den der Stand gilt — ein anderer Satz hat keinen. */
    hinweisId: string;
    /**
     * Zeitpunkt der Zustellung an den HAUPTEMPFAENGER (Bauherr), ISO — die
     * Grundlage fuer „Stempel nachholen". `null`, solange nur Kopien
     * hinausgingen: eine Anzeige, die nur den Architekten erreicht hat, wird
     * nicht gestempelt (Vertrag `VersandErgebnis.versendetAm`).
     */
    versendetAm: string | null;
    /** Erreichte Adressen in der Schreibung des Versandergebnisses. */
    erreicht: string[];
    /** Empfaenger des letzten Laufs, die NICHT erreicht wurden — nur an sie darf erneut gesendet werden. */
    nichtErreicht: VersandEmpfaenger[];
}

const klein = (s: string | null | undefined): string => sauber(s).toLowerCase();

/**
 * Stand nach einem Versandlauf. Erreichte Adressen kommen hinzu, der
 * Zeitpunkt der ersten Zustellung an den Hauptempfaenger bleibt; nicht
 * erreicht sind die Empfaenger dieses Laufs, die auch jetzt nicht erreicht
 * wurden (Reihenfolge bleibt — der Hauptempfaenger steht weiter vorn).
 * Erreicht der Lauf niemanden, bleibt der bisherige Stand (bzw. `null`) —
 * ein Lauf ohne Erfolg sperrt nichts.
 */
export const versandStandAus = (
    vorher: VersandStand | null,
    hinweisId: string,
    gesendetAn: ReadonlyArray<VersandEmpfaenger>,
    ergebnis: { versendetAn: ReadonlyArray<string>; versendetAm: string | null },
): VersandStand | null => {
    const neu = (ergebnis.versendetAn || []).map(sauber).filter(Boolean);
    const alt = vorher && vorher.hinweisId === hinweisId ? vorher : null;
    if (!neu.length) return alt;
    const erreicht = [...(alt ? alt.erreicht : [])];
    const gesehen = new Set(erreicht.map(klein));
    neu.forEach((e) => {
        if (!gesehen.has(klein(e))) { gesehen.add(klein(e)); erreicht.push(e); }
    });
    return {
        hinweisId,
        versendetAm: (alt && alt.versendetAm) || ergebnis.versendetAm || null,
        erreicht,
        nichtErreicht: gesendetAn.filter((e) => !gesehen.has(klein(e.email))),
    };
};

/**
 * Wie der naechste Lauf zu fuehren ist:
 *   erstversand   — noch niemand erreicht: an alle, Stempel nach Zustellung an den Bauherrn
 *   bauherr_offen — nur Kopien erreicht: an die Nicht-Erreichten (Bauherr vorn), Stempel nach seiner Zustellung
 *   nur_kopien    — Bauherr erreicht: an die Nicht-Erreichten OHNE neuen Stempel — der erste Zeitpunkt bleibt
 */
export type LaufArt = 'erstversand' | 'bauherr_offen' | 'nur_kopien';

export const laufArtAus = (stand: VersandStand | null): LaufArt =>
    !stand ? 'erstversand' : stand.versendetAm ? 'nur_kopien' : 'bauherr_offen';

/** Die Schritte des Versandwegs (`planungVersand.ts`), hereingereicht — so laeuft die Reihenfolge mit Attrappen im Test. */
export interface HinweisLaufSchritte {
    /** Versand ohne Stempel (`versende`). */
    versende: (auftrag: VersandAuftrag) => Promise<VersandErgebnis>;
    /** Versand mit Stempel nach Zustellung an den Bauherrn (`versendeBedenkenanzeige`). */
    versendeMitStempel: (auftrag: VersandAuftrag) => Promise<BedenkenanzeigeErgebnis>;
    /** Stempel nachholen (`stempleBedenkenanzeige`) — immer mit dem ERSTEN Zeitpunkt. */
    stemple: (versendetAm: string) => Promise<BedenkenStempel>;
    /** Gespeichertes `versendet_am` lesen — fuer `schon_versendet`, wenn ein anderer zuerst gestempelt hat. Optional. */
    leseVersendetAm?: () => Promise<string | null>;
}

export interface HinweisLaufErgebnis {
    laufArt: LaufArt;
    /** `null`, wenn der Lauf VOR dem Versand abbrach (Vorab-Stempel gescheitert) — es ging nichts hinaus. */
    versand: VersandErgebnis | null;
    /** Stempel dieses Laufs (Vorab-Stempel bei `nur_kopien`, sonst der Versandstempel); `null`, wenn nichts zu stempeln war. */
    stempel: BedenkenStempel | null;
    stand: VersandStand | null;
    /** `versendet_am`, das Editor und Aufrufer uebernehmen duerfen — nur, wenn es dem gespeicherten Stand entspricht; sonst `null`. */
    uebernehmen: string | null;
}

/**
 * Bei `schon_versendet` zaehlt der GESPEICHERTE Zeitpunkt (der Guard im
 * UPDATE hat ihn behalten), nicht der mitgegebene — ein anderer Lauf kann
 * einen frueheren gesetzt haben. Ohne lesbaren Wert wird NICHTS uebernommen
 * (Codex-Review Runde 3): lieber keine Anzeige als eine falsche; der Stempel
 * steht ja, und ein Neuladen zeigt ihn.
 */
const gespeicherterZeitpunkt = async (
    schritte: Pick<HinweisLaufSchritte, 'leseVersendetAm'>,
): Promise<string | null> => {
    if (!schritte.leseVersendetAm) return null;
    try {
        const db = await schritte.leseVersendetAm();
        return db || null;
    } catch {
        // Lesen ist Komfort — der Stempel steht; dann wird nichts uebernommen.
        return null;
    }
};

export interface StempelNachholErgebnis {
    stempel: BedenkenStempel;
    /** `versendet_am`, das zu uebernehmen ist: bei `gestempelt` der gesetzte, bei
     *  `schon_versendet` NUR der gelesene gespeicherte Wert; sonst `null`. */
    uebernehmen: string | null;
}

/**
 * „Stempel nachholen" — die Mail ist raus, nur `versendet_am` fehlt. Gestempelt
 * wird mit dem Zeitpunkt der ERSTEN Zustellung an den Bauherrn. Stand schon
 * ein Datum (`schon_versendet`), liefert der Service den MITGEGEBENEN
 * Zeitpunkt zurueck — der darf nicht ungeprueft in Bestand und Entwurf: es
 * gilt der gespeicherte, gelesen aus der Datenbank, oder nichts.
 */
export const stempelNachholen = async (
    versendetAm: string,
    schritte: Pick<HinweisLaufSchritte, 'stemple' | 'leseVersendetAm'>,
): Promise<StempelNachholErgebnis> => {
    const stempel = await schritte.stemple(versendetAm);
    if (stempel.status === 'gestempelt') return { stempel, uebernehmen: stempel.versendetAm || versendetAm };
    if (stempel.status === 'schon_versendet') return { stempel, uebernehmen: await gespeicherterZeitpunkt(schritte) };
    return { stempel, uebernehmen: null };
};

/**
 * EIN Versandlauf eines Hinweises — die Reihenfolge, die Zeitpunkt und
 * Hauptempfaenger schuetzt (Codex-Review Stufe 4, Runde 2):
 *
 *   - `nur_kopien` (Bauherr schon erreicht): KEIN neuer Stempel. Fehlt der
 *     Stempel noch, wird er VORAB mit dem ersten Zeitpunkt gesetzt; scheitert
 *     das, geht keine Kopie hinaus. Dann `versende` OHNE Stempel — denn im
 *     Wiederholungslauf stuende die erste Kopie als „Hauptempfaenger" und
 *     `versendeBedenkenanzeige` braechte einen zweiten Zeitpunkt mit, den der
 *     Guard zwar nicht schreibt, der aber als `schon_versendet` zurueckkaeme
 *     und so in Bestand und Entwurf landete. Der erste Zeitpunkt bleibt
 *     (`versandStandAus`), nur erreicht/nichtErreicht aendern sich.
 *   - sonst: `versendeMitStempel`; uebernommen wird nur ein in DIESEM Lauf
 *     gesetzter Stempel bzw. bei `schon_versendet` der gespeicherte Wert.
 */
export const fuehreHinweisLauf = async (p: {
    hinweisId: string;
    stand: VersandStand | null;
    /** `versendet_am` steht schon in der Akte (Bestand oder lokal uebernommen). */
    bereitsGestempelt: boolean;
    auftrag: VersandAuftrag;
    schritte: HinweisLaufSchritte;
}): Promise<HinweisLaufErgebnis> => {
    const { hinweisId, stand, bereitsGestempelt, auftrag, schritte } = p;
    const laufArt = laufArtAus(stand);

    if (laufArt === 'nur_kopien' && stand && stand.versendetAm) {
        let stempel: BedenkenStempel | null = null;
        let uebernehmen: string | null = null;
        if (!bereitsGestempelt) {
            // Vorab-Stempel nach derselben Regel wie „Stempel nachholen".
            const nachgeholt = await stempelNachholen(stand.versendetAm, schritte);
            stempel = nachgeholt.stempel;
            if (stempel.status === 'fehler') return { laufArt, versand: null, stempel, stand, uebernehmen: null };
            uebernehmen = nachgeholt.uebernehmen;
        }
        const versand = await schritte.versende(auftrag);
        return { laufArt, versand, stempel, stand: versandStandAus(stand, hinweisId, auftrag.empfaenger, versand), uebernehmen };
    }

    const { versand, stempel } = await schritte.versendeMitStempel(auftrag);
    const neu = versandStandAus(stand, hinweisId, auftrag.empfaenger, versand);
    let uebernehmen: string | null = null;
    if (stempel.status === 'gestempelt' && stempel.versendetAm) uebernehmen = stempel.versendetAm;
    else if (stempel.status === 'schon_versendet') uebernehmen = await gespeicherterZeitpunkt(schritte);
    return { laufArt, versand, stempel, stand: neu, uebernehmen };
};

/** Die Felder, die auf dem Blatt und in der Mail landen — nur sie entscheiden, ob gespeichert werden muss. */
const BLATT_FELDER: ReadonlyArray<keyof NeuerPlanungshinweis> = [
    'art', 'betreff', 'sachverhalt', 'gefaehrdung', 'folgen', 'rechtsgrundlage', 'forderung',
    'frist_am', 'folge_bei_fristablauf', 'empfaenger_name', 'empfaenger_email', 'adressat_ist_bauherr',
    'verteiler', 'frage_codes', 'erstellt_von_name',
];

const normal = (v: unknown): string => {
    if (v == null) return '';
    if (Array.isArray(v)) return JSON.stringify(v.map((x) => sauber(String(x))).filter(Boolean));
    if (typeof v === 'string') return v.trim();
    return JSON.stringify(v);
};

/**
 * Weicht der Entwurf in einem Blattfeld vom gespeicherten Satz ab? Dann darf
 * nicht versendet werden: das Blatt entstuende aus dem Entwurf, die Akte
 * truege den alten Stand — zwei Wahrheiten ueber denselben Versand.
 * `reaktion` zaehlt nicht, sie steht nicht auf dem Blatt.
 */
export const hinweisUngespeichert = (
    entwurf: NeuerPlanungshinweis | null | undefined,
    bestand: Planungshinweis | null | undefined,
): boolean => {
    if (!entwurf || !bestand) return true;
    return BLATT_FELDER.some((f) => normal(entwurf[f]) !== normal(bestand[f]));
};
